/**
 * Stable, opaque identity for this browser.
 *
 * Random, generated locally, and never linked to anything personal — the same approach
 * RideBeacon uses for its devices. It exists only so the server can attach one alert to one
 * phone; losing it costs a re-subscribe and nothing else.
 *
 * Inside the iOS shell the id comes from the Keychain instead. `localStorage` in a `WKWebView` is
 * thrown away on every reinstall, and a sideloaded app is reinstalled constantly — an identity that
 * changed that often would orphan its own telemetry every few days.
 */

import { shellDeviceId } from "./native";

const STORAGE_KEY = "gotime.deviceId";

export function getDeviceId(): string {
	const fromShell = shellDeviceId();
	if (fromShell) {
		return fromShell;
	}
	try {
		const existing = localStorage.getItem(STORAGE_KEY);
		if (existing && /^[a-f0-9]{32}$/.test(existing)) {
			return existing;
		}
		const bytes = crypto.getRandomValues(new Uint8Array(16));
		const id = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
		localStorage.setItem(STORAGE_KEY, id);
		return id;
	} catch {
		// Private browsing: a per-session id still lets alerts work until the tab closes.
		return [...crypto.getRandomValues(new Uint8Array(16))].map((byte) => byte.toString(16).padStart(2, "0")).join("");
	}
}
